import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useApi } from "@/lib/useApi";
import { api, type Scheme } from "@/lib/api";
import { BenefitChip, LevelChip, Chip } from "@/lib/chips";

type Audience = "startup" | "msme" | "small-biz" | "individual" | "";
type Need     = "loan" | "grant" | "equity" | "workspace" | "recognition" | "procurement" | "";

interface Props {
  onOpenScheme: (id: string) => void;
  onAskAi: (prompt: string) => void;
}

/**
 * Path Finder — "The Founder's Compass".
 *
 * Three questions (building as / need / state) arranged as directional
 * readings on a compass. The needle rotates as answers arrive, giving
 * the reader a visible sense of narrowing-in on their stack.
 */

const AUDIENCES: Array<[Audience, string, string]> = [
  ["startup",     "Tech startup",                "N"],
  ["msme",        "MSME / industry",             "NE"],
  ["small-biz",   "Small business",              "E"],
  ["individual",  "Individual / first-gen",      "SE"],
];
const NEEDS: Array<[Need, string, string]> = [
  ["loan",        "Loan / credit",               "S"],
  ["grant",       "Grant / subsidy",             "SW"],
  ["equity",      "Equity / VC",                 "W"],
  ["workspace",   "Incubator / workspace",       "NW"],
  ["recognition", "Recognition / tax relief",    "N"],
  ["procurement", "Govt customer base",          "NE"],
];

// Compass bearings for the needle rotation animation.
const BEARINGS: Record<string, number> = {
  N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315,
};

export function PathFinder({ onOpenScheme, onAskAi }: Props) {
  const { data: statesData } = useApi(() => api.states(), []);
  const [audience, setAudience] = useState<Audience>("");
  const [need, setNeed]         = useState<Need>("");
  const [state, setState]       = useState<string>("");
  const [results, setResults]   = useState<Scheme[] | null>(null);
  const [running, setRunning]   = useState(false);
  const canRun = !!(audience || need);

  // Average the two chosen bearings to pick the needle angle. If only one
  // is set, use that bearing. If neither, needle rests on N.
  const needleAngle = useMemo(() => {
    const aDir = AUDIENCES.find(([v]) => v === audience)?.[2];
    const nDir = NEEDS.find(([v]) => v === need)?.[2];
    const bearings = [aDir, nDir].filter(Boolean).map((d) => BEARINGS[d!]);
    if (bearings.length === 0) return 0;
    if (bearings.length === 1) return bearings[0];
    const avg = (bearings[0] + bearings[1]) / 2;
    return avg;
  }, [audience, need]);

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const r = await api.recommend({ audience, need, state });
      setResults(r.items);
    } catch (e) {
      console.error(e);
    } finally {
      setRunning(false);
    }
  };

  const askIntro = () => {
    const bits: string[] = [];
    if (audience) bits.push(`I'm building a ${audience}`);
    if (need)     bits.push(`I need ${need}`);
    if (state)    bits.push(`based in ${state}`);
    const intro = bits.length ? bits.join(", ") + "." : "I'm starting a business in India.";
    onAskAi(
      `${intro} Walk me step-by-step through the best central + state schemes ` +
        `and portals to apply to first. Be specific with amounts and URLs.`,
    );
  };

  const answered = (audience ? 1 : 0) + (need ? 1 : 0) + (state ? 1 : 0);

  return (
    <section id="path" className="relative max-w-6xl mx-auto px-5 py-24">
      <div aria-hidden className="absolute inset-x-10 inset-y-8 -z-10 bg-gradient-to-br from-amber-500/[0.05] via-transparent to-coral-500/[0.05] rounded-[3rem] blur-[2px]" />

      <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
        <div>
          <p className="section-eyebrow mb-2">chapter · two</p>
          <h2 className="section-title">
            The Founder&rsquo;s <span className="font-serif italic text-amber-300">Compass</span>.
          </h2>
          <p className="text-cream-100/60 mt-2 max-w-xl text-sm">
            Three readings. One heading. Your exact schemes, portals and
            incubators — ranked to the way you&rsquo;re actually building.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-cream-100/50">
          <span>{answered} / 3 readings taken</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.05fr] gap-10 items-start">
        {/* =====================================================
           Compass — the three directional questions
           ===================================================== */}
        <div className="relative rounded-[2rem] border border-amber-400/20 bg-plum-900/45 p-6 md:p-8 overflow-hidden">
          <CompassRose angle={needleAngle} locked={!!audience && !!need} />

          <div className="relative mt-8 space-y-7">
            <Reading
              roman="I"
              title="I am building as"
              direction="north"
              options={AUDIENCES.map(([v, lbl]) => [v, lbl] as [string, string])}
              active={audience}
              onPick={(v) => setAudience(audience === v ? "" : (v as Audience))}
            />
            <Reading
              roman="II"
              title="What I need"
              direction="south"
              options={NEEDS.map(([v, lbl]) => [v, lbl] as [string, string])}
              active={need}
              onPick={(v) => setNeed(need === v ? "" : (v as Need))}
            />
            <div>
              <ReadingLabel roman="III" title="Based in" direction="meridian" />
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="mt-2 w-full bg-plum-950/60 border border-white/10 text-cream-50 rounded-xl px-4 py-3 text-sm font-sans
                           focus:outline-none focus:border-amber-400/50 hover:border-white/20 transition"
              >
                <option value="" className="bg-plum-950">All India · no preference</option>
                {statesData?.items.map((s) => (
                  <option key={s.state} value={s.state} className="bg-plum-950">
                    {s.state} ({s.total})
                  </option>
                ))}
              </select>
            </div>

            <button
              disabled={!canRun || running}
              onClick={run}
              className="group w-full relative inline-flex items-center justify-center gap-3 py-4 rounded-xl
                         bg-cream-50 text-plum-950 font-display font-semibold text-sm tracking-wide
                         hover:bg-amber-300 disabled:opacity-35 disabled:cursor-not-allowed transition"
            >
              <span className="font-serif italic text-base">✦</span>
              {running ? "Taking bearings…" : "Point the compass"}
              <span aria-hidden className="font-serif italic">→</span>
            </button>
          </div>
        </div>

        {/* =====================================================
           Results — "The Dispatch" panel
           ===================================================== */}
        <div className="relative rounded-[2rem] border border-amber-400/20 bg-plum-900/35 p-6 md:p-8 min-h-[360px]">
          <div className="flex items-center justify-between mb-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/85">
              · vārtā ·
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream-100/45">
              dated {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </span>
          </div>

          {!results ? (
            <EmptyDispatch />
          ) : results.length === 0 ? (
            <p className="text-cream-100/55 text-sm text-center py-16">
              No direct bearing. Try broader readings — or ask Ākāśavāṇī below.
            </p>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <p className="font-display text-xl text-cream-50 leading-snug mb-2">
                <span className="font-serif italic text-amber-300">Bearings set.</span>{" "}
                {results.length} scheme{results.length === 1 ? "" : "s"} on course for{" "}
                {[audience, need && `need: ${need}`, state].filter(Boolean).join(" · ") || "you"}.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream-100/40 mb-5">
                ranked by fit · tap any line
              </p>
              <div className="space-y-2.5">
                {results.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => onOpenScheme(s.id)}
                    className="group w-full text-left rounded-xl border border-white/8 hover:border-amber-400/40 bg-plum-950/40 hover:bg-plum-950/65 p-4 transition"
                  >
                    <div className="flex items-start gap-3">
                      <span className="font-serif italic text-amber-300/70 text-lg leading-none mt-0.5 shrink-0 w-6 text-center">
                        {i + 1}.
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <BenefitChip kind={s.benefit_type} />
                          <LevelChip level={s.level} />
                          {s.state && <Chip>{s.state}</Chip>}
                        </div>
                        <div className="font-display text-[15px] font-semibold text-cream-50 group-hover:text-amber-200 transition">
                          {s.name}
                        </div>
                        {s.summary && (
                          <div className="text-xs text-cream-100/55 mt-1 leading-snug">
                            {s.summary.length > 180 ? s.summary.slice(0, 180) + "…" : s.summary}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={askIntro}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl
                           bg-gradient-to-r from-amber-400 to-coral-500 hover:from-amber-300 hover:to-amber-500
                           text-plum-950 font-display font-semibold text-sm tracking-wide transition"
              >
                <span className="font-serif italic">✦</span>
                Ask Ākāśavāṇī to walk me through this
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- */
/* The compass — rotating needle + cardinal marks                    */
/* --------------------------------------------------------------- */
function CompassRose({ angle, locked }: { angle: number; locked: boolean }) {
  return (
    <div className="relative h-28 flex items-center justify-center">
      <svg viewBox="0 0 200 200" className="h-28 w-28 shrink-0">
        <defs>
          <radialGradient id="compass-face" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#faf6ec" stopOpacity="0.12" />
            <stop offset="70%" stopColor="#1b1738" stopOpacity="0.0" />
          </radialGradient>
          <linearGradient id="needle-n" x1="50%" y1="100%" x2="50%" y2="0%">
            <stop offset="0%" stopColor="#e63946" />
            <stop offset="100%" stopColor="#ffb703" />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="90" fill="url(#compass-face)" stroke="rgba(243,193,116,0.35)" strokeWidth="1" />
        <circle cx="100" cy="100" r="72" fill="none" stroke="rgba(243,193,116,0.15)" strokeWidth="0.5" strokeDasharray="2 4" />
        {/* Cardinal ticks */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <g key={a} transform={`rotate(${a} 100 100)`}>
            <line x1="100" y1="12" x2="100" y2={a % 90 === 0 ? 24 : 20}
                  stroke="rgba(243,193,116,0.6)" strokeWidth={a % 90 === 0 ? 1.5 : 0.7} />
          </g>
        ))}
        {/* N E S W labels */}
        {[
          { x: 100, y: 6,   t: "N" },
          { x: 196, y: 104, t: "E" },
          { x: 100, y: 200, t: "S" },
          { x: 4,   y: 104, t: "W" },
        ].map(({ x, y, t }) => (
          <text key={t} x={x} y={y}
                textAnchor="middle" dominantBaseline="middle"
                fontFamily="IBM Plex Serif" fontStyle="italic"
                fontSize="11" fill="rgba(243,193,116,0.8)">
            {t}
          </text>
        ))}

        {/* Needle group rotates with angle */}
        <motion.g
          animate={{ rotate: angle }}
          transition={{ type: "spring", stiffness: 70, damping: 14 }}
          style={{ transformOrigin: "100px 100px" }}
        >
          <polygon points="100,30 106,100 100,108 94,100" fill="url(#needle-n)" />
          <polygon points="100,170 106,100 100,92 94,100" fill="rgba(255,255,255,0.22)" />
        </motion.g>

        <circle cx="100" cy="100" r="4" fill="#faf6ec" stroke="#ffb703" strokeWidth="1.2" />
      </svg>

      <div className="ml-5 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/85">
          {locked ? "bearing · locked" : "take readings →"}
        </div>
        <div className="font-display text-xl text-cream-50 mt-0.5 tracking-tight">
          {locked ? `${Math.round(angle)}° heading` : "Awaiting readings"}
        </div>
        <div className="text-[12px] text-cream-100/50 mt-0.5">
          {locked
            ? "The compass points to your stack."
            : "Answer even one; the needle swings."}
        </div>
      </div>
    </div>
  );
}

/* Option-picker with roman numeral + direction hint */
function Reading({
  roman, title, direction, options, active, onPick,
}: {
  roman: string;
  title: string;
  direction: string;
  options: Array<[string, string]>;
  active: string;
  onPick: (v: string) => void;
}) {
  return (
    <div>
      <ReadingLabel roman={roman} title={title} direction={direction} />
      <div className="mt-2 grid grid-cols-2 gap-2">
        {options.map(([v, lbl]) => (
          <button
            key={v}
            type="button"
            onClick={() => onPick(v)}
            className={`text-left text-sm px-3 py-2.5 rounded-xl border transition font-sans
                        ${active === v
                          ? "bg-amber-400/20 border-amber-400/55 text-cream-50"
                          : "bg-plum-950/45 border-white/8 text-cream-100/80 hover:border-white/20 hover:text-cream-50"}`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReadingLabel({ roman, title, direction }: { roman: string; title: string; direction: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-serif italic text-amber-300 text-xl leading-none">{roman}.</span>
      <span className="font-display text-sm text-cream-50">{title}</span>
      <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.35em] text-cream-100/40">
        {direction}
      </span>
    </div>
  );
}

function EmptyDispatch() {
  return (
    <div className="relative h-full flex flex-col items-center justify-center text-center py-14">
      <svg viewBox="0 0 120 120" className="w-16 h-16 mb-5 opacity-60" aria-hidden>
        <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(243,193,116,0.4)" strokeWidth="1" strokeDasharray="2 3" />
        <circle cx="60" cy="60" r="3" fill="#ffb703" />
        <line x1="60" y1="18" x2="60" y2="58" stroke="#ffb703" strokeWidth="1.5" />
        <text x="60" y="12" textAnchor="middle" fontFamily="IBM Plex Serif" fontStyle="italic" fontSize="10" fill="rgba(243,193,116,0.8)">N</text>
      </svg>
      <p className="font-display text-lg text-cream-50/90 leading-snug">
        Vārtā is empty.
      </p>
      <p className="mt-1 text-sm text-cream-100/55 max-w-xs">
        Take a reading on the compass — even one answer swings the needle and fills this page.
      </p>
    </div>
  );
}
